import React from 'react';
import { Shield } from 'lucide-react';
import { countVulnerabilitiesBySeverity } from './CriticalityData';
import { ReportData } from '../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface VulnerabilitiesChartProps {
  summaryData: ReportData;
}

// Config de colores
const severityStyles: { [key: string]: { iconColor: string; bars: string[] } } = {
  Crítico: {
    iconColor: 'text-red-700',
    bars: ['bg-red-700', 'bg-red-700', 'bg-red-700', 'bg-red-600', 'bg-red-600'],
  },
  Alto: {
    iconColor: 'text-orange-600',
    bars: ['bg-orange-500', 'bg-orange-500', 'bg-orange-400', 'bg-orange-400', 'bg-orange-400'],
  },
  Medio: {
    iconColor: 'text-yellow-500',
    bars: ['bg-yellow-400', 'bg-yellow-400', 'bg-yellow-300', 'bg-yellow-300', 'bg-yellow-200'],
  },
  Bajo: {
    iconColor: 'text-green-500',
    bars: ['bg-green-400', 'bg-green-400', 'bg-green-300', 'bg-green-300', 'bg-green-200'],
  },
};

const SeverityIcon: React.FC<{ level: 'Crítico' | 'Alto' | 'Medio' | 'Bajo' }> = ({ level }) => {
  const { iconColor, bars } = severityStyles[level];
  return (
    <div className="flex flex-col items-center space-y-1">
      <Shield className={`h-10 w-10 ${iconColor}`} />
      <div className="flex space-x-1">
        {bars.map((barColor, index) => (
          <div key={index} className={`w-2 h-5 ${barColor} rounded-sm`} />
        ))}
      </div>
    </div>
  );
};

const VulnerabilitiesChart: React.FC<VulnerabilitiesChartProps> = ({ summaryData }) => {
  const { t } = useTranslation();
  const severityCounts = countVulnerabilitiesBySeverity(summaryData);
  
  const data = [
    { label: 'Crítico', count: severityCounts.critical },
    { label: 'Alto', count: severityCounts.high },
    { label: 'Medio', count: severityCounts.medium },
    { label: 'Bajo', count: severityCounts.low },
  ];

  return (
    <div className="grid grid-cols-4 gap-4 mt-6">
      {data.map((severity) => (
        <div
          key={severity.label}
          className="flex flex-col items-center p-4 border rounded-lg shadow-md bg-white"
        >
          <div className="text-3xl mb-2">
            <SeverityIcon level={severity.label as 'Crítico' | 'Alto' | 'Medio' | 'Bajo'} />
          </div>
          <div className="text-2xl font-bold">{severity.count}</div>
          <div className="text-lg font-semibold">{t(`vulnerabilitiesChart.${severity.label.toLowerCase()}`)}</div>
        </div>
      ))}
    </div>
  );
};

export default VulnerabilitiesChart;
